import { useEffect, useRef } from "react";
import {
  useAPI,
  useWebSocketListener,
  WebSocketChannel,
} from "@huub-web/backend";

import type {
  BackendGenerationAction,
  BackendPhoto,
  GenerationAction,
  GenerationStatus,
  InteriorDesignerAction,
  InteriorDesignerWsMessage,
  Photo,
} from "../context/types";
import { refetchVocab } from "./useVocab";

/**
 * Transforms composite frontend GenerationAction[] into flat
 * BackendGenerationAction[] for POST /generate.
 *
 * Frontend actions are per-panel (e.g. InteriorFinishingAction has
 * floor + wall). Backend expects one action per target_panel.
 */
export function toBackendActions({
  actions,
}: {
  actions: GenerationAction[];
}): BackendGenerationAction[] {
  const result: BackendGenerationAction[] = [];

  for (const action of actions) {
    switch (action.type) {
      case "interior_finishing": {
        const extraWensen = action.extra_wensen ?? "";
        let isFirst = true;

        if (action.floor) {
          result.push({
            target_panel: "floor",
            chip_id: action.floor,
            free_text: isFirst ? extraWensen : "",
          });
          isFirst = false;
        }

        if (action.wall) {
          result.push({
            target_panel: "wall",
            chip_id: action.wall,
            free_text: isFirst ? extraWensen : "",
          });
        }

        if (!action.floor && !action.wall && extraWensen) {
          result.push({
            target_panel: "free_wish",
            chip_id: null,
            free_text: extraWensen,
          });
        }
        break;
      }

      case "furnishing": {
        const extraWensen = action.extra_wensen ?? "";
        let isFirst = true;

        if (action.style) {
          result.push({
            target_panel: "style",
            chip_id: action.style,
            free_text: isFirst ? extraWensen : "",
          });
          isFirst = false;
        }

        if (action.room_type) {
          result.push({
            target_panel: "room_type",
            chip_id: action.room_type,
            free_text: isFirst ? extraWensen : "",
          });
        }

        if (!action.style && !action.room_type && extraWensen) {
          result.push({
            target_panel: "free_wish",
            chip_id: null,
            free_text: extraWensen,
          });
        }
        break;
      }

      case "wall_color": {
        const freeText = [action.custom_text, action.extra_wensen]
          .filter(Boolean)
          .join(". ");

        if (action.ral_code) {
          result.push({
            target_panel: "wall_color",
            chip_id: action.ral_code,
            free_text: freeText,
          });
        } else if (action.custom_text) {
          result.push({
            target_panel: "free_wish",
            chip_id: null,
            free_text: freeText,
          });
        } else if (action.extra_wensen) {
          result.push({
            target_panel: "free_wish",
            chip_id: null,
            free_text: action.extra_wensen,
          });
        }
        break;
      }

      case "remove_furniture":
        result.push({
          target_panel: "empty_room",
          chip_id: null,
          free_text: "",
        });
        break;

      case "other_wishes":
        result.push({
          target_panel: "free_wish",
          chip_id: null,
          free_text: action.text,
        });
        break;

      case "day_night":
        console.warn(
          "[useGeneration] DayNightAction has no backend mapping — skipped",
        );
        break;
    }
  }

  return result;
}

/**
 * Maps a backend WS photo (from the `ready` event) to the
 * frontend Photo type.
 *
 * Key differences from backend shape:
 * - `created` → `created_at`
 * - Flat classification fields → nested `classification` object
 * - Backend `actions` replaced with original frontend-format actions
 * - Extra fields (`updated`, `project_id`, etc.) dropped
 */
export function toFrontendPhoto({
  backendPhoto,
  frontendActions,
}: {
  backendPhoto: BackendPhoto;
  frontendActions: GenerationAction[] | null;
}): Photo {
  const blobUrl = backendPhoto.blob_url ?? "";
  return {
    id: backendPhoto.id,
    origin: backendPhoto.origin,
    blob_url: blobUrl,
    thumbnail_url: backendPhoto.thumbnail_url ?? blobUrl,
    parent_photo_id: backendPhoto.parent_photo_id,
    actions: frontendActions,
    classification: {
      room_type: backendPhoto.room_type,
      room_condition: backendPhoto.room_condition,
      furnished: backendPhoto.furnished,
      inside_or_outside: backendPhoto.inside_or_outside,
    },
    deleted_at: backendPhoto.deleted_at,
    created_at: backendPhoto.created,
  };
}

type UseGenerationParams = {
  /**
   * Generation slice from InteriorDesignerState
   */
  generation: {
    status: GenerationStatus;
    generationId: string | null;
    actions: GenerationAction[] | null;
    parentPhotoId: string | null;
  };
  /**
   * Reducer dispatch — stable reference from useReducer
   */
  dispatch: (action: InteriorDesignerAction) => void;
  /**
   * Current vocab version string from useVocab, or null if not loaded
   */
  vocabVersion: string | null;
};

/**
 * Integrates with the backend generation pipeline:
 * - REST: POST /generate on status === "loading"
 * - WS: listens on INTERIOR_DESIGNER_GENERATION for progress/result/error
 */
export const useGeneration = ({
  generation,
  dispatch,
  vocabVersion,
}: UseGenerationParams) => {
  const { $post } = useAPI();

  const $postRef = useRef($post);
  $postRef.current = $post;

  const vocabVersionRef = useRef(vocabVersion);
  vocabVersionRef.current = vocabVersion;

  const generationRef = useRef<{
    generationId: string | null;
    actions: GenerationAction[] | null;
  }>({
    generationId: generation.generationId,
    actions: generation.actions,
  });
  const statusRef = useRef(generation.status);

  useEffect(() => {
    const prevStatus = statusRef.current;
    statusRef.current = generation.status;

    const justEnteredLoading =
      generation.status === "loading" && prevStatus !== "loading";

    generationRef.current = {
      generationId:
        generation.status === "loading" &&
        generation.generationId === null &&
        !justEnteredLoading
          ? generationRef.current.generationId
          : generation.generationId,
      actions: generation.actions,
    };
  }, [generation.status, generation.generationId, generation.actions]);

  useEffect(() => {
    if (generation.status !== "loading") return;
    if (!generation.actions || !generation.parentPhotoId) return;

    const backendActions = toBackendActions({ actions: generation.actions });

    if (backendActions.length === 0) {
      dispatch({
        type: "GENERATION_FAILED",
        generationId: null,
        errorMessage: "No valid actions to send",
        errorCode: "request_failed",
      });
      return;
    }

    let cancelled = false;

    $postRef
      .current(
        `/interior-designer/photo/${generation.parentPhotoId}/generate`,
        {
          actions: backendActions,
          fe_vocab_version: vocabVersionRef.current,
        },
      )
      .then((response: { data: { generation_id: string } }) => {
        if (cancelled) return;
        dispatch({
          type: "GENERATION_STARTED",
          generationId: response.data.generation_id,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const resp = (
          error as {
            response?: { status?: number; data?: { detail?: unknown } };
          }
        )?.response;
        const status = resp?.status;
        const detail = resp?.data?.detail;
        const detailStr = typeof detail === "string" ? detail : "";

        if (status === 409 && detailStr.startsWith("Vocab version mismatch")) {
          refetchVocab();
          dispatch({
            type: "GENERATION_FAILED",
            generationId: null,
            errorMessage: detailStr,
            errorCode: "vocab_version_mismatch",
          });
        } else if (status === 409) {
          dispatch({
            type: "GENERATION_FAILED",
            generationId: null,
            errorMessage:
              detailStr || "Another generation is already in flight",
            errorCode: "generation_in_flight",
          });
        } else {
          dispatch({
            type: "GENERATION_FAILED",
            generationId: null,
            errorMessage: detailStr || "Request failed",
            errorCode: "request_failed",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    generation.status,
    generation.actions,
    generation.parentPhotoId,
    dispatch,
  ]);

  useWebSocketListener<InteriorDesignerWsMessage>(
    WebSocketChannel.INTERIOR_DESIGNER_GENERATION,
    (message) => {
      if (statusRef.current !== "loading") return;

      const { data } = message;
      const ref = generationRef.current;

      if (
        ref.generationId !== null &&
        data.generation_id !== ref.generationId
      ) {
        return;
      }

      switch (data.status) {
        case "started":
          // Ref-only write: WS `started` can arrive before HTTP 202 sets generationId.
          // Eagerly capture it so subsequent WS events pass the ID filter.
          if (ref.generationId === null) {
            generationRef.current = {
              ...generationRef.current,
              generationId: data.generation_id,
            };
          }
          break;

        case "pending":
          dispatch({
            type: "GENERATION_PROGRESS",
            generationId: data.generation_id,
            message: data.progress_message,
            elapsedSeconds: data.elapsed_seconds,
          });
          break;

        case "ready":
          dispatch({
            type: "GENERATION_SUCCEEDED",
            generationId: data.generation_id,
            photo: toFrontendPhoto({
              backendPhoto: data.photo,
              frontendActions: ref.actions,
            }),
          });
          break;

        case "failed":
          dispatch({
            type: "GENERATION_FAILED",
            generationId: data.generation_id,
            errorMessage: data.error_message,
            errorCode: data.error_code,
          });
          break;
      }
    },
  );
};
